import React, { useState, useCallback, useEffect } from 'react'
import { InfoTip } from './InfoTip'
import { getLotSize, getQty, formatLotsQty } from '../utils/lotSize'
import type { RsiStraddleConfig, TrendingExpiryConfig } from '../types'
import { useAuth } from '../context/AuthContext'
import { API_BASE } from '../utils/api'

/* ─── Strategy metadata ─── */

const strategyMeta: Record<string, { label: string; icon: string; color: string }> = {
  rsi_straddle: { label: 'RSI Straddle', icon: '⟐', color: '#38bdf8' },
  trending_expiry: { label: 'Trending Expiry', icon: '⏱', color: '#a78bfa' },
}

/* ─── Spec-aligned defaults (used as fallback if API unavailable) ─── */

export const rsiDefaults: RsiStraddleConfig = {
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50,
  entryTime: '09:30', noReentryAfter: '14:30', noReentryAfterStrict: '14:30', squareOffTime: '15:20',
  slPercent: 0.20, exitPercent: 0.40, minPremiumSl: 10, premiumFloor: 50,
  maxReentryCount: 2, marginPerLot: 150000,
  reentryRsiThresholdHigh: 60, reentryRsiCooldown: 55, reentryRsiPeriod: 14,
  reentryRsiTimeframe: '3min', candleTimeframe: '10min',
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
  executionMode: 'scheduled' as const,
}

export const trendDefaults: TrendingExpiryConfig = {
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50, extraLegStrikeOffset: 1,
  entryStartTime: '09:30', entryEndTime: '14:30', entryIntervalMinutes: 30, squareOffTime: '15:20',
  callSLPercent: 0.80, putSLPercent: 0.70, profitTargetPercent: 0.90,
  extraLegSLPercent: 0.50, extraLegTargetPercent: 0.90, extraLegOncePerSide: true,
  extraLegSameQty: true, extraLegQuantityLots: 1,
  perStraddleLossPercent: 0.02, totalStrategyLossPercent: 0.03, marginPerLot: 150000,
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
  executionMode: 'scheduled' as const,
}

/* ═══════════════════════════════════════════════════════════════════
   TRADER SET SIDEBAR — Self-Service Strategy Setup
   
   Trader can:
   1. Select strategy type + scrip (underlying index)
   2. Set user-editable parameters (delta, qty, re-entry, entry time)
   3. Give a custom name to the strategy
   4. Choose: Save / Schedule / Execute Instantly
   
   Admin-locked parameters shown as read-only below.
   ═══════════════════════════════════════════════════════════════════ */

interface TraderSetSidebarProps {
  /** Which strategy type to pre-select (optional) */
  initialStratType?: 'rsi_straddle' | 'trending_expiry'
  /** Pre-fill data for editing an existing execution */
  editData?: {
    id: string
    setName: string
    stratType: 'rsi_straddle' | 'trending_expiry'
    underlying: string
    lots: number
    params: Record<string, any>
  }
  onClose: () => void
  onCreate: (data: {
    stratType: 'rsi_straddle' | 'trending_expiry'
    underlying: string
    lots: number
    broker: string
    mode: 'single_client' | 'multi_client'
    executionMode: 'scheduled' | 'instant'
    customName: string
    params: Record<string, any>
  }) => void
  /** Called when editing an existing execution (PUT update) */
  onUpdate?: (id: string, data: {
    set_name: string
    underlying: string
    params: Record<string, any>
  }) => void
}

export function TraderSetSidebar({ initialStratType, editData, onClose, onCreate, onUpdate }: TraderSetSidebarProps) {
  const isEdit = !!editData
  const [stratType, setStratType] = useState<'rsi_straddle' | 'trending_expiry'>(editData?.stratType || initialStratType || 'rsi_straddle')
  const meta = strategyMeta[stratType]

  // Admin defaults (fetched from API or hardcoded fallback)
  const [adminDefaults, setAdminDefaults] = useState<Record<string, any>>(
    stratType === 'rsi_straddle' ? rsiDefaults : trendDefaults
  )
  const [defaultsSource, setDefaultsSource] = useState<'loading' | 'admin' | 'platform_default'>('loading')
  const p = adminDefaults

  // ── Trader-editable fields ──
  const { session } = useAuth()
  const [customName, setCustomName] = useState(editData?.setName || '')
  const [underlying, setUnderlying] = useState(editData?.underlying || adminDefaults.underlying)
  const [lots, setLots] = useState(editData?.lots || adminDefaults.quantityLots)
  const [qty, setQty] = useState(getQty(editData?.lots || adminDefaults.quantityLots, editData?.underlying || adminDefaults.underlying))
  const [broker, setBroker] = useState(session?.mappedBroker?.brokerId || '')
  const [mode, setMode] = useState<'single_client' | 'multi_client'>('single_client')
  const [executionMode, setExecutionMode] = useState<'scheduled' | 'instant'>(
    editData?.params?.executionMode || 'scheduled'
  )
  const [brokerList, setBrokerList] = useState<Array<{ id: string; name: string }>>([])

  // ── New editable fields ──
  const [targetDelta, setTargetDelta] = useState<number>(editData?.params?.targetDelta ?? adminDefaults.targetDelta ?? 0.50)
  const [strikeOffset, setStrikeOffset] = useState<number>(editData?.params?.strikeOffset ?? adminDefaults.strikeOffset ?? 0)
  const [strikeMode, setStrikeMode] = useState<string>(editData?.params?.strikeSelectionMode ?? adminDefaults.strikeSelectionMode ?? 'ATM')
  const [reentryEnabled, setReentryEnabled] = useState<boolean>(editData?.params?.reentryEnabled !== false)
  const [maxReentries, setMaxReentries] = useState<number>(editData?.params?.maxReentryCount ?? adminDefaults.maxReentryCount ?? 2)
  const [entryTime, setEntryTime] = useState<string>(
    editData?.params?.entryTime || editData?.params?.entryStartTime || adminDefaults.entryTime || adminDefaults.entryStartTime || '09:30'
  )

  // ── Fetch admin defaults from API ──
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const token = session?.token
        if (!token) return
        const res = await fetch(`${API_BASE}/api/strategies/defaults/${stratType}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json() as any
          if (data?.params) {
            setAdminDefaults(data.params)
            setDefaultsSource(data.source || 'admin')
            // Update editable fields if not in edit mode
            if (!isEdit) {
              setTargetDelta(data.params.targetDelta ?? 0.50)
              setStrikeOffset(data.params.strikeOffset ?? 0)
              setStrikeMode(data.params.strikeSelectionMode ?? 'ATM')
              setMaxReentries(data.params.maxReentryCount ?? 2)
              setEntryTime(data.params.entryTime || data.params.entryStartTime || '09:30')
            }
          }
        }
      } catch {
        setDefaultsSource('platform_default')
      }
    }
    fetchDefaults()
  }, [stratType, session?.token])

  // Fetch brokers
  useEffect(() => {
    if (session?.mappedBroker) {
      setBroker(session.mappedBroker.brokerId)
      return
    }
    const fetchBrokers = async () => {
      try {
        const token = session?.token
        if (!token) return
        const res = await fetch(`${API_BASE}/api/brokers`, { headers: { 'Authorization': `Bearer ${token}` } })
        if (res.ok) {
          const data = await res.json() as Array<{ id: string; name: string }>
          if (data && Array.isArray(data)) {
          setBrokerList(data)
          if (data.length > 0 && !broker) setBroker(data[0].id)
        }
        }
      } catch (e) {
        console.warn('Failed to fetch brokers for sidebar', e)
      }
    }
    fetchBrokers()
  }, [session?.token])

  // Sync qty ↔ lots
  const handleQtyChange = (newQty: number) => {
    const lotSize = getLotSize(underlying)
    const clampedQty = Math.max(lotSize, newQty)
    const newLots = Math.max(1, Math.round(clampedQty / lotSize))
    setQty(newLots * lotSize)
    setLots(newLots)
  }
  const handleLotsChange = (newLots: number) => {
    const clamped = Math.max(1, newLots)
    setLots(clamped)
    setQty(getQty(clamped, underlying))
  }
  const handleUnderlyingChange = (newUnderlying: string) => {
    setUnderlying(newUnderlying)
    setQty(getQty(lots, newUnderlying))
  }

  // Build trader overrides (user-editable params merged with admin defaults)
  const buildParams = useCallback(() => {
    const overrides: Record<string, any> = {
      ...adminDefaults,
      quantityLots: lots,
      targetDelta,
      strikeOffset,
      strikeSelectionMode: strikeMode,
      reentryEnabled,
      executionMode,
      underlying,
    }
    if (stratType === 'rsi_straddle') {
      overrides.maxReentryCount = reentryEnabled ? maxReentries : 0
      overrides.entryTime = entryTime
      overrides.noReentryAfter = adminDefaults.noReentryAfter || '14:30'
    } else {
      overrides.entryStartTime = entryTime
      overrides.entryEndTime = adminDefaults.entryEndTime || '14:30'
    }
    return overrides
  }, [adminDefaults, lots, targetDelta, strikeOffset, strikeMode, reentryEnabled, maxReentries, entryTime, executionMode, stratType, underlying])

  const handleSubmit = useCallback(() => {
    if (isEdit && onUpdate && editData) {
      onUpdate(editData.id, {
        set_name: customName || editData.setName,
        underlying,
        params: buildParams(),
      })
    } else {
      onCreate({
        stratType, underlying, lots, broker, mode, executionMode,
        customName: customName || `${meta.label} — ${underlying}`,
        params: buildParams(),
      })
    }
  }, [stratType, underlying, lots, broker, mode, executionMode, customName, buildParams, isEdit, editData, onUpdate, onCreate, meta.label])

  /* ─── Format helpers ─── */
  const pct = (v: number) => `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%`

  /* ─── Build locked sections based on selected strategy ─── */
  const lockedSections: { icon: string; title: string; items: [string, string][] }[] = [
    {
      icon: '📋', title: 'Instrument & Position',
      items: [
        ['Strategy', meta.label],
        ['Expiry', p.expiryType],
        ['Position', p.positionType === 'straddle' ? 'Straddle' : 'Strangle'],
        ...(stratType === 'trending_expiry' ? [['Adj Offset', `${(p as TrendingExpiryConfig).extraLegStrikeOffset} OTM`] as [string, string]] : []),
      ],
    },
    {
      icon: '⏱', title: 'Timing',
      items: stratType === 'rsi_straddle'
        ? [
          ['Square-off', p.squareOffTime + ' IST'],
          ['No Re-entry After', (p as RsiStraddleConfig).noReentryAfter + ' IST'],
        ]
        : [
          ['Last Entry', (p as TrendingExpiryConfig).entryEndTime + ' IST'],
          ['Interval', `Every ${(p as TrendingExpiryConfig).entryIntervalMinutes} min`],
          ['Square-off', p.squareOffTime + ' IST'],
        ],
    },
    {
      icon: '🛡', title: 'Risk Management',
      items: stratType === 'rsi_straddle'
        ? [
          ['Stop Loss', pct(p.slPercent)],
          ['Target', pct((p as RsiStraddleConfig).exitPercent)],
          ['Min SL', `₹${(p as RsiStraddleConfig).minPremiumSl}`],
          ['Premium Floor', `₹${(p as RsiStraddleConfig).premiumFloor}`],
          ['Margin/Lot', `₹${p.marginPerLot?.toLocaleString()}`],
        ]
        : [
          ['Call SL', pct((p as TrendingExpiryConfig).callSLPercent)],
          ['Put SL', pct((p as TrendingExpiryConfig).putSLPercent)],
          ['Target', pct((p as TrendingExpiryConfig).profitTargetPercent)],
          ['Adj Leg SL', pct((p as TrendingExpiryConfig).extraLegSLPercent)],
          ['Adj Target', pct((p as TrendingExpiryConfig).extraLegTargetPercent)],
          ['Adj Qty', (p as TrendingExpiryConfig).extraLegSameQty ? 'Same as Entry' : formatLotsQty((p as TrendingExpiryConfig).extraLegQuantityLots, underlying)],
          ['Set Breaker', pct((p as TrendingExpiryConfig).perStraddleLossPercent)],
          ['Global Breaker', pct((p as TrendingExpiryConfig).totalStrategyLossPercent)],
          ['Margin/Lot', `₹${p.marginPerLot?.toLocaleString()}`],
        ],
    },
    {
      icon: '🔧', title: 'Advanced',
      items: [
        ...(stratType === 'rsi_straddle' ? [
          ['VIX RSI <', String((p as RsiStraddleConfig).reentryRsiThresholdHigh)],
          ['Cooldown <', String((p as RsiStraddleConfig).reentryRsiCooldown)],
          ['RSI Period', `${(p as RsiStraddleConfig).reentryRsiPeriod} candles`],
          ['RSI TF', (p as RsiStraddleConfig).reentryRsiTimeframe],
          ['SL Trail TF', (p as RsiStraddleConfig).candleTimeframe],
        ] as [string, string][] : []),
        ['Order Type', p.orderType],
        ['Event Skip', p.eventDaySkip ? 'Yes' : 'No'],
      ],
    },
  ]

  /* ─── Inline styles ─── */
  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 0.625rem', marginTop: '1rem',
    background: 'rgba(255,255,255,0.025)', borderRadius: '6px 6px 0 0',
    borderBottom: '1px solid var(--border)',
    fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    color: 'var(--text-muted)',
  }

  const kvRowStyle = (isEven: boolean): React.CSSProperties => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.375rem 0.75rem',
    background: isEven ? 'rgba(255,255,255,0.015)' : 'transparent',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  })

  const kvLabelStyle: React.CSSProperties = {
    fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 500,
  }

  const kvValueStyle: React.CSSProperties = {
    fontSize: '0.6875rem', fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 600, color: 'var(--text-primary)',
  }

  return (
    <div className="strat-sidebar-overlay" onClick={onClose}>
      <div className="strat-sidebar-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="strat-sidebar-header">
          <h3>
            <span style={{ color: meta.color, marginRight: '0.5rem' }}>{meta.icon}</span>
            {isEdit ? `Edit ${meta.label}` : `+ New ${meta.label}`}
          </h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="strat-sidebar-body">

          {/* ═══ EDITABLE — Trader's config ═══ */}
          <div style={{
            padding: '0.625rem', marginBottom: '0.25rem',
            background: 'rgba(56,189,248,0.06)', borderRadius: '6px',
            border: '1px solid rgba(56,189,248,0.15)',
          }}>
            <div style={{
              fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', color: 'var(--accent)', marginBottom: '0.625rem',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}>
              ✏️ Your Configuration
              <span style={{ fontSize: '0.5625rem', fontWeight: 500, background: 'rgba(56,189,248,0.15)', padding: '0.125rem 0.375rem', borderRadius: '3px', color: 'var(--accent)' }}>EDITABLE</span>
            </div>
            <div className="cfg-grid">

              {/* Custom Strategy Name */}
              <div className="cfg-field" style={{ gridColumn: '1 / -1' }}>
                <label className="cfg-label">Strategy Name</label>
                <input type="text" className="cfg-input" value={customName}
                  placeholder={`${meta.label} — ${underlying}`}
                  onChange={(e) => setCustomName(e.target.value)}
                  style={{ fontWeight: 600 }} />
                <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  Give your strategy a memorable name
                </span>
              </div>

              {/* Strategy Type Selector */}
              <div className="cfg-field">
                <label className="cfg-label">Strategy Type</label>
                <select className="cfg-input" value={stratType} onChange={(e) => setStratType(e.target.value as any)}
                  disabled={isEdit}
                  style={{ fontWeight: 600, color: strategyMeta[stratType]?.color }}>
                  <option value="rsi_straddle">⟐ RSI Straddle</option>
                  <option value="trending_expiry">⏱ Trending Expiry</option>
                </select>
              </div>

              <div className="cfg-field">
                <label className="cfg-label">Underlying <InfoTip term="underlying" /></label>
                <select className="cfg-input" value={underlying} onChange={(e) => handleUnderlyingChange(e.target.value)}>
                  <option value="NIFTY">NIFTY 50</option>
                  <option value="BANKNIFTY">BANKNIFTY</option>
                  <option value="FINNIFTY">FINNIFTY</option>
                  <option value="MIDCPNIFTY">MIDCAPNIFTY</option>
                  <option value="SENSEX">SENSEX</option>
                  <option value="BANKEX">BANKEX</option>
                </select>
              </div>

              <div className="cfg-field">
                <label className="cfg-label">Quantity <InfoTip term="lots" /></label>
                <input type="number" className="cfg-input" value={qty} min={getLotSize(underlying)} step={getLotSize(underlying)}
                  onChange={(e) => handleQtyChange(parseInt(e.target.value) || getLotSize(underlying))} />
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  lot size: {getLotSize(underlying)}
                </span>
              </div>

              <div className="cfg-field">
                <label className="cfg-label">Lots per Leg <InfoTip term="lots" /></label>
                <input type="number" className="cfg-input" value={lots} min={1} max={999}
                  onChange={(e) => handleLotsChange(parseInt(e.target.value) || 1)} />
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  = {getQty(lots, underlying)} qty
                </span>
              </div>

              {/* ── Strike Selection ── */}
              {/* Current strategies (RSI Straddle & Trending Expiry) = ATM only.
                  Future strategies may support OTM/ITM/DELTA selection. */}
              {stratType !== 'rsi_straddle' && stratType !== 'trending_expiry' ? (
                <>
                  <div className="cfg-field">
                    <label className="cfg-label">Strike Selection</label>
                    <select className="cfg-input" value={strikeMode} onChange={(e) => setStrikeMode(e.target.value)}>
                      <option value="ATM">ATM (At The Money)</option>
                      <option value="OTM">OTM (Out of Money)</option>
                      <option value="ITM">ITM (In The Money)</option>
                      <option value="DELTA">Delta Based</option>
                    </select>
                  </div>
                  {strikeMode === 'DELTA' ? (
                    <div className="cfg-field">
                      <label className="cfg-label">Target Delta</label>
                      <input type="number" className="cfg-input" value={targetDelta} min={0.01} max={1.00} step={0.01}
                        onChange={(e) => setTargetDelta(parseFloat(e.target.value) || 0.50)} />
                      <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                        0.01 – 1.00
                      </span>
                    </div>
                  ) : (
                    <div className="cfg-field">
                      <label className="cfg-label">Strike Offset</label>
                      <input type="number" className="cfg-input" value={strikeOffset} min={-5} max={5} step={1}
                        onChange={(e) => setStrikeOffset(parseInt(e.target.value) || 0)} />
                      <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                        0 = ATM, +1 = 1 OTM, -1 = 1 ITM
                      </span>
                    </div>
                  )}
                </>
              ) : null}

              {/* ── Entry Time ── */}
              <div className="cfg-field">
                <label className="cfg-label">Entry Time (IST)</label>
                <input type="time" className="cfg-input" value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                  style={{ fontFamily: "'JetBrains Mono', monospace" }} />
                <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  {stratType === 'rsi_straddle' ? 'Initial straddle entry time' : 'First straddle entry time'}
                </span>
              </div>

              {/* ── Re-entry (RSI Straddle only) ── */}
              {stratType === 'rsi_straddle' && (
                <>
                  <div className="cfg-field">
                    <label className="cfg-label">Re-entry</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="button" onClick={() => setReentryEnabled(true)} style={{
                        flex: 1, padding: '0.375rem 0.5rem', borderRadius: '5px', border: 'none',
                        cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                        background: reentryEnabled ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.06)',
                        color: reentryEnabled ? '#4ade80' : 'var(--text-muted)',
                        outline: reentryEnabled ? '1px solid #4ade80' : '1px solid transparent',
                      }}>✓ Enabled</button>
                      <button type="button" onClick={() => setReentryEnabled(false)} style={{
                        flex: 1, padding: '0.375rem 0.5rem', borderRadius: '5px', border: 'none',
                        cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                        background: !reentryEnabled ? 'rgba(248,113,113,0.2)' : 'rgba(255,255,255,0.06)',
                        color: !reentryEnabled ? '#f87171' : 'var(--text-muted)',
                        outline: !reentryEnabled ? '1px solid #f87171' : '1px solid transparent',
                      }}>✕ Disabled</button>
                    </div>
                  </div>
                  {reentryEnabled && (
                    <div className="cfg-field">
                      <label className="cfg-label">Max Re-entries</label>
                      <input type="number" className="cfg-input" value={maxReentries} min={0} max={10}
                        onChange={(e) => setMaxReentries(parseInt(e.target.value) || 0)} />
                      <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                        Total trades = 1 initial + {maxReentries} re-entries
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Execution Mode Toggle */}
              <div className="cfg-field" style={{ gridColumn: '1 / -1' }}>
                <label className="cfg-label">Execution Mode</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setExecutionMode('scheduled')} style={{
                    flex: 1, padding: '0.375rem 0.5rem', borderRadius: '5px', border: 'none',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                    background: executionMode === 'scheduled' ? 'rgba(56,189,248,0.25)' : 'rgba(255,255,255,0.06)',
                    color: executionMode === 'scheduled' ? '#38bdf8' : 'var(--text-muted)',
                    outline: executionMode === 'scheduled' ? '1px solid #38bdf8' : '1px solid transparent',
                  }}>📅 Scheduled</button>
                  <button type="button" onClick={() => setExecutionMode('instant')} style={{
                    flex: 1, padding: '0.375rem 0.5rem', borderRadius: '5px', border: 'none',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                    background: executionMode === 'instant' ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.06)',
                    color: executionMode === 'instant' ? '#4ade80' : 'var(--text-muted)',
                    outline: executionMode === 'instant' ? '1px solid #4ade80' : '1px solid transparent',
                  }}>⚡ Instant</button>
                </div>
                {executionMode === 'instant' && (
                  <span style={{ fontSize: '0.6875rem', color: '#4ade80', marginTop: '0.25rem', display: 'block' }}>
                    Entry time bypassed — enters market immediately on start
                  </span>
                )}
                {executionMode === 'scheduled' && (
                  <span style={{ fontSize: '0.6875rem', color: '#38bdf8', marginTop: '0.25rem', display: 'block' }}>
                    Strategy activates at entry time. Recommended for expiry days.
                  </span>
                )}
              </div>

              <div className="cfg-field" style={{ gridColumn: '1 / -1' }}>
                <label className="cfg-label">Broker Account</label>
                <div className="cfg-input" style={{ background: 'rgba(56,189,248,0.08)', cursor: 'default', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 700 }}>{session?.mappedBroker?.brokerName || 'SVIS'}</span>
                  <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>•</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--accent)' }}>
                    {session?.mappedBroker?.clientId || 'No Client ID mapped'}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Single Client Mode
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ LOCKED — Admin-set params (compact, changes with strategy type) ═══ */}
          {lockedSections.map((sec, si) => (
            <div key={`${stratType}-${si}`}>
              <div style={sectionHeaderStyle}>
                <span>{sec.icon}</span> {sec.title}
                <span style={{
                  fontSize: '0.5625rem', fontWeight: 600, marginLeft: 'auto',
                  background: 'rgba(255,255,255,0.06)', padding: '0.125rem 0.5rem',
                  borderRadius: '3px', letterSpacing: '0.03em',
                }}>🔒 ADMIN SET</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {sec.items.map(([label, value], j) => (
                  <div key={j} style={kvRowStyle(j % 2 === 0)}>
                    <span style={kvLabelStyle}>{label}</span>
                    <span style={kvValueStyle}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Defaults source indicator */}
          <div style={{
            padding: '0.5rem 0.75rem', marginTop: '0.75rem',
            fontSize: '0.625rem', color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: '0.375rem',
          }}>
            {defaultsSource === 'admin' ? '✅ Admin-configured defaults' : defaultsSource === 'platform_default' ? '📦 Platform defaults' : '⏳ Loading defaults...'}
          </div>

        </div>

        {/* Footer */}
        <div className="strat-sidebar-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            {isEdit ? '💾 Save Changes' : '🚀 Create & Start'}
          </button>
        </div>
      </div>
    </div>
  )
}
